import { PositionAnalysis } from '@/types/portfolio';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { TrendingUp } from 'lucide-react';

interface PortfolioGainsPieChartProps {
  analyses: PositionAnalysis[];
}

const COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
  '#e11d48', '#84cc16',
];

export function PortfolioGainsPieChart({ analyses }: PortfolioGainsPieChartProps) {
  const gainsData = analyses
    .filter(analysis => (analysis.addedValueChf || 0) > 0)
    .map(analysis => ({
      name: analysis.ticker,
      value: analysis.addedValueChf || 0,
      percentage: 0,
    }));

  if (gainsData.length === 0) return null;

  const totalGains = gainsData.reduce((sum, item) => sum + item.value, 0);

  const TOP = 12;
  const sorted = [...gainsData].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, TOP);
  const othersValue = sorted.slice(TOP).reduce((s, i) => s + i.value, 0);
  const withOthers = othersValue > 0 ? [...top, { name: 'Others', value: othersValue, percentage: 0 }] : top;

  const chartData = withOthers.map(item => ({
    ...item,
    percentage: ((item.value / totalGains) * 100).toFixed(1),
  }));

  // Render label on slices that are large enough
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, percentage }: any) => {
    const pct = parseFloat(percentage);
    if (pct < 4) return null; // skip tiny slices
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {name}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg shadow-xl p-3 min-w-[160px]">
          <p className="font-bold text-sm text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground mt-1">
            CHF {data.value.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs font-semibold text-primary mt-0.5">{data.percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="w-full relative z-10 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Portfolio Gains Distribution
        </CardTitle>
        <CardDescription>
          Added value contribution by stock — CHF {totalGains.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total gains
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row items-start gap-6">
          {/* Pie chart */}
          <div className="w-full lg:w-1/2 h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderLabel}
                  innerRadius={55}
                  outerRadius={145}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="hsl(var(--background))"
                  strokeWidth={2}
                >
                  {chartData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {chartData.map((entry, index) => (
              <div
                key={`legend-${index}`}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-muted/40 border border-border/50 hover:bg-muted/70 transition-colors"
              >
                <div
                  className="w-3.5 h-3.5 rounded-sm flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground truncate">{entry.name}</p>
                  <p className="text-xs text-muted-foreground">
                    CHF {entry.value.toLocaleString('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    <span className="ml-1.5 font-semibold text-primary">{entry.percentage}%</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
